import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from './ui/Dialog';
import { Button } from './ui/Button';
import { apiClient } from '../lib/api-client';
import { Info, AlertCircle } from 'lucide-react';
import { Skeleton } from './ui/Skeleton';

interface ActionDefinition {
  action: string;
  label: string;
}

interface PagePermission {
  displayName: string;
  actions: ActionDefinition[];
  description: string;
}

interface PermissionFlag {
  label: string;
  description: string;
}

interface PermissionSchema {
  pages: Record<string, PagePermission>;
  flags: Record<string, PermissionFlag>;
}

interface RbacSchemaDialogProps {
  open: boolean;
  onClose: () => void;
}

export function RbacSchemaDialog({ open, onClose }: RbacSchemaDialogProps) {
  const [schema, setSchema] = useState<PermissionSchema | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && !schema) {
      fetchSchema();
    }
  }, [open]);

  const fetchSchema = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const response = await apiClient.get('/api/admin/permission-schema');
      setSchema(response.schema);
    } catch (err: any) {
      console.error('Failed to fetch permission schema:', err);
      setError(err.message || 'Failed to load RBAC schema');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[900px] max-h-[90vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>RBAC Schema</DialogTitle>
        </DialogHeader>

        {/* Single scrollable area for all content */}
        <div className="flex-1 overflow-y-auto px-1">
          <div className="space-y-6 py-4">
            {isLoading && (
              <div className="space-y-4">
                <Skeleton className="h-20 w-full" />
                <Skeleton className="h-32 w-full" />
                <Skeleton className="h-32 w-full" />
              </div>
            )}

            {error && (
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
              </div>
            )}

            {!isLoading && !error && schema && (
              <>
                {/* Read-only notice */}
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 flex items-start gap-3">
                  <Info className="h-5 w-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-medium text-blue-900 dark:text-blue-100 mb-1">Read-Only Schema</h3>
                    <p className="text-sm text-blue-800 dark:text-blue-200">
                      This schema is managed in code and requires a deployment to modify. 
                      Changes ensure OPA policies stay in sync with permission definitions.
                    </p>
                  </div>
                </div>

                {/* Page Permissions Section */}
                <div>
                  <div className="mb-4">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Page Permissions</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      Permissions that control access to different pages in the portal
                    </p>
                  </div>

                  <div className="space-y-4">
                    {Object.entries(schema.pages).map(([pageKey, page]) => (
                      <div
                        key={pageKey}
                        className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4 space-y-3"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                {page.displayName}
                              </h3>
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-mono font-medium bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                                {pageKey}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-400">{page.description}</p>
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">Actions:</h4>
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                            {page.actions.map((actionDef) => (
                              <div
                                key={actionDef.action}
                                className="flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-600"
                              >
                                <span className="text-sm font-medium text-gray-900 dark:text-white">
                                  {actionDef.label}
                                </span>
                                <span className="text-xs font-mono text-gray-500 dark:text-gray-400">
                                  ({actionDef.action})
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Permission Flags Section */}
                <div>
                  <div className="mb-4">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Permission Flags</h2>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      Special flags that grant broad permissions across the portal
                    </p>
                  </div>

                  <div className="space-y-4">
                    {Object.entries(schema.flags).map(([flagKey, flag]) => (
                      <div
                        key={flagKey}
                        className="bg-gray-50 dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg p-4"
                      >
                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-2">
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                                {flag.label}
                              </h3>
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded text-xs font-mono font-medium bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300">
                                {flagKey}
                              </span>
                            </div>
                            <p className="text-sm text-gray-600 dark:text-gray-400">{flag.description}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        <DialogFooter className="flex-shrink-0">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

